import {
  ApolloClient,
  createHttpLink,
  from,
  InMemoryCache,
  NormalizedCacheObject,
  type Reference,
  type StoreObject,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { shouldSilenceLoggedOutMeError } from "@modules/apollo/authError.util";
import { normalizeAuthToken } from "@modules/authentication/authToken";

export function buildApolloClient() {
  const backendHost = import.meta.env.VITE_BACKEND_HOST;

  if (!backendHost) {
    throw new Error("No backend host provided.");
  }

  const httpLink = createHttpLink({
    uri: `${backendHost}/graphql`,
  });

  const authLink = setContext((_, { headers }) => {
    const token = normalizeAuthToken(localStorage.getItem("authToken"));

    return {
      headers: {
        ...headers,
        ...(headers?.Authorization
          ? {}
          : { Authorization: token ? `Bearer ${token}` : "" }),
      },
    };
  });

  const errorLink = onError(
    ({ graphQLErrors, networkError, operation, response }) => {
      if (
        shouldSilenceLoggedOutMeError({
          operationName: operation.operationName,
          graphQLErrors,
          networkError,
        })
      ) {
        return;
      }

      const networkErrorDetails = networkError as
        | {
            name?: string;
            message?: string;
            statusCode?: number;
            bodyText?: string;
            result?: unknown;
            response?: {
              status?: number;
              statusText?: string;
            };
          }
        | undefined;
      const fullError = {
        operationName: operation.operationName,
        variables: operation.variables,
        graphQLErrors: graphQLErrors?.map((err) => ({
          message: err.message,
          path: err.path,
          locations: err.locations,
          extensions: err.extensions,
        })),
        networkError: networkErrorDetails
          ? {
              name: networkErrorDetails.name,
              message: networkErrorDetails.message,
              statusCode:
                networkErrorDetails.statusCode ??
                networkErrorDetails.response?.status ??
                null,
              bodyText: networkErrorDetails.bodyText ?? null,
              result: networkErrorDetails.result ?? null,
              response: networkErrorDetails.response
                ? {
                    status: networkErrorDetails.response.status,
                    statusText: networkErrorDetails.response.statusText,
                  }
                : null,
            }
          : null,
        responseErrors: response?.errors ?? null,
      };

      console.error("[Apollo][Error]", fullError);
      console.error(
        "[Apollo][Error][JSON]",
        JSON.stringify(fullError, null, 2)
      );
    }
  );

  const createArrayMergeFunction = () => {
    return (existing = [], incoming: any[]) => {
      if (incoming && Object.keys(incoming).length === 0) {
        return existing;
      }
      return incoming;
    };
  };

  return new ApolloClient<NormalizedCacheObject>({
    link: from([errorLink, authLink, httpLink]),
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            customers: {
              merge(existing, incoming) {
                return incoming;
              },
            },
            featureExtractionExportRuns: {
              keyArgs: ["where", "order"],
              merge(existing, incoming, { args, readField }) {
                const existingNodes = existing?.nodes ?? [];
                const incomingNodes = incoming?.nodes ?? [];
                const seen = new Set<string>();
                const mergeNodes = (nodes: readonly (Reference | StoreObject)[]) =>
                  nodes.filter((node) => {
                    const id = readField<string>("id", node);
                    if (!id || !seen.has(id)) {
                      if (id) {
                        seen.add(id);
                      }
                      return true;
                    }
                    return false;
                  });

                if (args?.after) {
                  return {
                    ...incoming,
                    nodes: mergeNodes([...existingNodes, ...incomingNodes]),
                  };
                }

                if (!existing) {
                  return incoming;
                }

                const hasLoadedOlderPages =
                  existingNodes.length > incomingNodes.length;
                return {
                  ...incoming,
                  nodes: mergeNodes([...incomingNodes, ...existingNodes]),
                  pageInfo: hasLoadedOlderPages
                    ? existing.pageInfo
                    : incoming.pageInfo,
                };
              },
            },
          },
        },
        PESTest: {
          fields: {
            results: {
              keyArgs: false,
              merge(existing: any, incoming: any, { args }: any) {
                if (!args?.after) {
                  return incoming;
                }
                return {
                  ...incoming,
                  nodes: [...(existing?.nodes ?? []), ...(incoming?.nodes ?? [])],
                };
              },
            },
          },
        },
        Customer: {
          fields: {
            organisations: {
              merge: createArrayMergeFunction(),
            },
            erus: {
              merge: createArrayMergeFunction(),
            },
            cassettes: {
              merge: createArrayMergeFunction(),
            },
          },
        },
      },
    }),
  });
}
